import { clsx, type ClassValue } from 'clsx';

/**
 * Combines class names with intelligent conditional class handling.
 *
 * This utility uses clsx to handle conditional classes, making it easy to
 * apply dynamic styling based on component state. Perfect for use with
 * Tailwind CSS and DaisyUI classes.
 *
 * @example
 * // Basic usage
 * cn('px-4 py-2', 'text-white', 'bg-blue-500')
 * // => 'px-4 py-2 text-white bg-blue-500'
 *
 * @example
 * // Conditional classes
 * cn('btn', isLoading && 'loading', isDisabled && 'btn-disabled')
 *
 * @example
 * // Object syntax
 * cn('base', {
 *   'bg-primary': isPrimary,
 *   'bg-secondary': !isPrimary,
 * })
 *
 * @example
 * // Array syntax
 * cn(['btn', variant && `btn-${variant}`])
 *
 * @example
 * // With DaisyUI components
 * <button className={cn('btn', size && `btn-${size}`, className)}>
 *   Click me
 * </button>
 *
 * @example
 * // Common patterns
 * cn(
 *   'base-class',           // Always applied
 *   condition && 'class-1', // Conditionally applied
 *   {
 *     'class-2': bool1,     // Object syntax
 *     'class-3': bool2,
 *   },
 *   className               // Props pass-through
 * )
 *
 * @param inputs - Class names, conditionals, arrays, or objects to merge
 * @returns Merged class string with falsy values filtered out
 */
export function cn(...inputs: ClassValue[]): string {
  return clsx(inputs);
}
